import React from 'react';
import { Box, Typography, Button, Modal, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import apiClient from '../../../../api/apiClient'; // Import the API client
import extractApiError from '../../../../api/extractApiError';
import { ACTION_BLUE } from '../../../../theme/theme';

interface DeactivateAppModalProps {
  open: boolean;
  onClose: () => void;
  publisherId: string; // شناسه ناشر
  appId: string; // شناسه اپلیکیشن
  active: boolean; // وضعیت فعال/غیرفعال
  /** Receives the `active` value the server persisted, not a guess. */
  onConfirm: (active: boolean) => void;
}

const DeactivateAppModal: React.FC<DeactivateAppModalProps> = ({
  open,
  onClose,
  publisherId,
  appId,
  active,
  onConfirm,
}) => {
  const queryClient = useQueryClient();

  const handleConfirm = async () => {
    try {
      const response = await apiClient.post(
        `/v2/publisher/applications/changeStatus`,
        null,
        {
          params: {
            publisher_id: publisherId,
            application_id: appId,
            active: active ? 0 : 1,
          },
        },
      );

      // Hand the server's own value back rather than letting the caller
      // guess: flipping a local flag left the button disagreeing with the
      // database until the next reload.
      onConfirm(Number(response.data?.application?.active) === 1);

      // The details query caches for five minutes, so without this the page
      // still serves the pre-change copy after a reload.
      queryClient.invalidateQueries({
        queryKey: ['applicationDetails', publisherId, appId],
      });
    } catch (error) {
      // Without this the button silently did nothing on failure.
      console.error('Error changing application status:', error);
      toast.error(extractApiError(error, 'خطا در تغییر وضعیت اپلیکیشن'));
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          height: 200,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 24,
          p: 16,
          m: 8,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            textAlign: 'right',
            color: '#25282E',
            fontSize: 16,
            fontFamily: 'IRANYekanXFaNum',
            fontWeight: 700,
            lineHeight: '20px',
            m: 16,
          }}
        >
          {active ? 'غیرفعال‌سازی اپلیکیشن' : 'فعال‌سازی اپلیکیشن'}
        </Typography>

        <Typography
          sx={{
            mt: 2,
            textAlign: 'right',
            color: '#60646E',
            fontSize: 14,
            fontFamily: 'IRANYekanXFaNum',
            fontWeight: 600,
          }}
        >
          {active
            ? 'با غیرفعال‌سازی و عدم انتشار نسخه‌ای از اپلیکیشن، اپلیکیشن از روی پلتفرم سیبچه برداشته خواهد شد!'
            : 'با فعال‌سازی اپلیکیشن، اپلیکیشن دوباره در پلتفرم سیبچه منتشر خواهد شد.'}
        </Typography>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 16 }}
        >
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#FFD6DB',
              color: '#EF4056',
              fontSize: 14,
              fontFamily: 'IRANYekanXFaNum',
              fontWeight: 700,
              '&:hover': {
                backgroundColor: '#FFD6DB',
              },
            }}
            onClick={handleConfirm} // استفاده از تابع handleConfirm
          >
            {active ? 'تایید و غیرفعال‌سازی' : 'تایید و فعال‌سازی'}
          </Button>

          <Button
            variant="contained"
            sx={{
              backgroundColor: ACTION_BLUE,
              color: 'white',
              fontSize: 12,
              fontFamily: 'IRANYekanXFaNum',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: ACTION_BLUE,
              },
            }}
            onClick={onClose}
          >
            انصراف
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default DeactivateAppModal;
